import React, { useState, useEffect, useRef, ChangeEvent, ReactNode } from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  TextField,
  Container,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  IconButton,
  Paper,
  Pagination,
  Divider,
  Card,
  CardContent,
  Alert,
  Toolbar,
  SelectChangeEvent,
  DialogContentText,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tab,
  Tabs,
  AppBar,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  Snackbar,
} from '@mui/material';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import {
  Add as AddIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Lock as LockIcon,
  VpnKey as VpnKeyIcon,
  Close as CloseIcon,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  MonitorOutlined as MonitorIcon,
  Refresh as RefreshIcon,
  Videocam as VideocamIcon,
  Dashboard as DashboardIcon,
  Pool as PoolIcon,
  Menu as MenuIcon,
  NotificationsActive as NotificationsActiveIcon,
} from '@mui/icons-material';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import ForumIcon from '@mui/icons-material/Forum';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import Badge from '@mui/material/Badge';
import { useOperatorMessages } from '../contexts/OperatorMessageContext';
import { useFacilityBoard } from '../contexts/FacilityBoardContext';
import { OperatorMessagesPanel } from './OperatorMessagesPage';
import { FacilityBoardPanel } from './FacilityBoardPanel';
import { ControlArchivePanel } from './ControlArchivePanel';
import axios from 'axios';
import { Pool, PoolCreate, PoolUpdate } from '../types/domain';
import PasswordManager from '../components/password/PasswordManager';
import { useApiService } from '../hooks/useApiService';
import { ApiResponse } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { usePoolFormModal } from '../hooks/usePoolFormModal';
import PoolForm from '../components/pool/PoolForm';
import { SystemSettings } from '../types/domain';
import { standardPoolApi } from '../services/standardPoolApi';
import SystemManagement from '../components/system/SystemManagement';
import { API_BASE_URL as STANDARD_API_BASE_URL } from '../config';
import CentralReportsPage from './reports/CentralReportsPage';
import CameraLayoutPlannerPage from './camera/CameraLayoutPlannerPage';
import CameraSpecWizardPage from './camera/CameraSpecWizardPage';
import LabelRecordingPanel from './LabelRecordingPanel';

// 새로 개발한 컴포넌트 가져오기
import { 
  StatsSummaryWidget, 
  PoolStatusWidget,
  EventLogWidget,
  WearableDataSummary
} from '../components/dashboard';
import SystemReadinessWidget from '../components/dashboard/SystemReadinessWidget';
import PoolManualAlertDialog, {
  type PoolManualAlertTarget,
} from '../components/central-control/PoolManualAlertDialog';
import SafetyAlertDispatchHistoryCard from '../components/safety/SafetyAlertDispatchHistoryCard';
import { ConnectionStatusIndicator, KymaBrandTitle } from '../components/common';
import api from '../services/api';
import { fetchSystemHealthSummary } from '../services/systemHealthService';
import { openCentralMonitoringPopupWindow } from '../components/monitoring/cctvPopupRoutes';
import VideoCameraFrontIcon from '@mui/icons-material/VideoCameraFront';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import { isSuperAdminUser } from '../utils/adminAccess';
import { useKeepGrantedTab, useMenuPolicy } from '../brand/MenuPolicyContext';
import { hiddenTabProps } from '../brand/menuCatalog';

const DRAWER_WIDTH = 240;

/**
 * 좌측 메뉴에 「카메라 배치·설치」 표시 여부.
 * false여도 라우트·탭 렌더(?tab=camera-layout)·CameraLayoutPlannerPage 코드는 유지.
 */
const SHOW_CAMERA_LAYOUT_NAV = false;
const POOL_SUB_MENU_IDS = ['nav.pools.list', 'nav.pools.status'] as const;

/** DB/API status(OPEN·active 혼재) · is_active 를 대시보드용 활성으로 판별 */
function isPoolActiveForDashboard(pool: {
  status?: string | null;
  is_active?: boolean | null;
}): boolean {
  if (pool.is_active === false) return false;
  const s = String(pool.status || '').trim().toLowerCase();
  if (['inactive', 'closed', 'disabled', 'off'].includes(s)) return false;
  if (['maintenance', 'maint'].includes(s)) return false;
  if (!s || ['active', 'open', 'operating', 'available', 'online'].includes(s)) {
    return true;
  }
  return pool.is_active === true;
}

/** UI Chip / filter 용으로 status 정규화 */
function normalizePoolDashboardStatus(pool: {
  status?: string | null;
  is_active?: boolean | null;
}): Pool['status'] {
  if (pool.is_active === false) return 'inactive';
  const s = String(pool.status || '').trim().toLowerCase();
  if (['inactive', 'closed', 'disabled', 'off'].includes(s)) return 'inactive';
  if (['maintenance', 'maint'].includes(s)) return 'maintenance';
  if (['active', 'open', 'operating', 'available', 'online', ''].includes(s)) {
    return 'active';
  }
  return isPoolActiveForDashboard(pool) ? 'active' : 'inactive';
}

const CentralControl: React.FC = () => {
  const [pools, setPools] = useState<Pool[]>([]);
  const [filteredPools, setFilteredPools] = useState<Pool[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [poolsLoaded, setPoolsLoaded] = useState(false);
  const statsRequestIdRef = useRef(0);
  const [error, setError] = useState<string | null>(null);
  const [searchCode, setSearchCode] = useState<string>('');
  const [filter, setFilter] = useState<string>('all');
  const [isPasswordManagerOpen, setIsPasswordManagerOpen] = useState(false);
  const [settings, setSettings] = useState<SystemSettings | null>(null);
  const [activeTab, setActiveTab] = useState<number>(0);
  /** 시설 관리 내부: 0=시설 목록, 1=시설 상태 */
  const [poolMgmtSubTab, setPoolMgmtSubTab] = useState<number>(0);
  const [lastRefreshed, setLastRefreshed] = useState<Date>(new Date());
  const theme = useTheme();
  const isMobileNav = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [manualAlertTarget, setManualAlertTarget] = useState<PoolManualAlertTarget | null>(null);
  const [manualAlertSnack, setManualAlertSnack] = useState<string | null>(null);
  const [systemStats, setSystemStats] = useState({
    poolCount: 0,
    activePoolCount: 0,
    cctvCount: 0,
    onlineCctvCount: 0,
    activeMembersCount: 0,
    totalMembersCount: 0,
    wearableCount: 0,
    activeWearableCount: 0,
    activeIncidents: 0,
    resolvedIncidents: 0,
    systemUptime: 0,
    dataCollectionRate: 0,
    readinessStatus: '…',
    readinessPass: 0,
    readinessTotal: 0,
    healthCheckSummary: '',
    centralAiJobCount: 0,
    centralAiEnabled: false,
    cctvStats: {
      total: 0,
      active: 0,
      maintenance: 0,
      inactive: 0,
    },
  });

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { logout, authState } = useAuth();
  const isSuperuser = isSuperAdminUser(authState.user);
  const { allowNav } = useMenuPolicy();
  const poolSubOn = useKeepGrantedTab(poolMgmtSubTab, POOL_SUB_MENU_IDS, setPoolMgmtSubTab);
  const { unreadCount } = useOperatorMessages();
  const { unreadCount: boardUnreadCount } = useFacilityBoard();
  const { poolApi } = useApiService();
  
  // 일반관리자: 개별 관제만 허용
  useEffect(() => {
    if (authState.loading) return;
    if (!authState.isAuthenticated) return;
    if (!isSuperuser) {
      navigate('/individual-monitoring', { replace: true });
    }
  }, [authState.loading, authState.isAuthenticated, isSuperuser, navigate]);

  // 임시 sendMessage 함수 (오류 방지용)
  const sendMessage = (type: string, data: any) => {
    console.log('[TEMP] sendMessage called in CentralControl:', type, data);
  };

  // API URL 설정
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || STANDARD_API_BASE_URL;

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'system') {
      setActiveTab(2);
    } else if (tab === 'camera-layout') {
      setActiveTab(4);
    } else if (tab === 'camera-spec-wizard') {
      setActiveTab(8);
    } else if (tab === 'label-recording') {
      setActiveTab(9);
    } else if (tab === 'messages') {
      setActiveTab(5);
    } else if (tab === 'facility-board') {
      setActiveTab(6);
    } else if (tab === 'control-archive') {
      setActiveTab(7);
    } else if (tab === 'pool-status') {
      setActiveTab(1);
      setPoolMgmtSubTab(1);
    } else if (tab === 'pools' || tab === 'pool-management') {
      setActiveTab(1);
      setPoolMgmtSubTab(0);
    } else if (tab === 'reports' || tab === 'report') {
      setActiveTab(3);
    } else {
      setActiveTab(0);
    }
  }, [searchParams]);

  // 실제 데이터 fetch 함수들
  const fetchCctvStats = async () => {
    try {
      const cctvs = await api.get<Array<{ status?: string }>>('/api/cctvs/');
      const list = Array.isArray(cctvs) ? cctvs : [];
      const online = list.filter(
        (cctv) => String(cctv.status || '').toLowerCase() === 'active'
      ).length;
      return {
        cctvCount: list.length,
        onlineCctvCount: online,
        cctvStats: {
          total: list.length,
          active: online,
          maintenance: list.filter(
            (c) => String(c.status || '').toLowerCase() === 'maintenance'
          ).length,
          inactive: list.filter((c) => {
            const s = String(c.status || '').toLowerCase();
            return s !== 'active' && s !== 'maintenance';
          }).length,
        },
      };
    } catch (error) {
      console.error('CCTV 통계 가져오기 실패:', error);
      return {
        cctvCount: 0,
        onlineCctvCount: 0,
        cctvStats: { total: 0, active: 0, maintenance: 0, inactive: 0 },
      };
    }
  };

  const fetchWearableStats = async () => {
    try {
      const response = await api.get<{ items?: { status?: string }[] }>(
        '/api/all-wearables/'
      );
      const wearables = response?.items || [];
      return {
        wearableCount: wearables.length,
        activeWearableCount: wearables.filter(
          (w) => String(w.status || '').toLowerCase() === 'active'
        ).length,
      };
    } catch (error) {
      console.error('웨어러블 통계 가져오기 실패:', error);
      return { wearableCount: 0, activeWearableCount: 0 };
    }
  };

  const fetchMemberStats = async () => {
    try {
      const response = await api.get<{ total?: number; active?: number }>(
        '/api/all-members/stats',
        undefined,
        { useCache: false, silentError: true }
      );
      return {
        totalMembersCount: Number(response?.total ?? 0),
        activeMembersCount: Number(response?.active ?? 0),
      };
    } catch (error) {
      console.error('회원 통계 가져오기 실패:', error);
      return { totalMembersCount: 0, activeMembersCount: 0 };
    }
  };

  const fetchSystemStats = async (poolList: Pool[] = pools) => {
    const requestId = ++statsRequestIdRef.current;
    try {
      setLoading(true);

      const poolStats = {
        poolCount: poolList.length,
        activePoolCount: poolList.filter((p) => isPoolActiveForDashboard(p)).length,
      };

      const [cctvStats, wearableStats, memberStats] = await Promise.all([
        fetchCctvStats(),
        fetchWearableStats(),
        fetchMemberStats(),
      ]);

      if (requestId !== statsRequestIdRef.current) return;

      const health = await fetchSystemHealthSummary({
        cctvCount: cctvStats.cctvCount,
        onlineCctvCount: cctvStats.onlineCctvCount,
      });

      if (requestId !== statsRequestIdRef.current) return;

      const failedChecks = health.checkLabels.filter((c) => !c.ok);
      const healthCheckSummary =
        health.readinessTotal > 0
          ? `준비 점검 ${health.readinessPass}/${health.readinessTotal}${
              failedChecks.length ? ` (${failedChecks.map((f) => f.label).join(', ')} 확인)` : ''
            }`
          : '';

      setSystemStats({
        ...poolStats,
        ...cctvStats,
        ...wearableStats,
        ...memberStats,
        activeIncidents: health.activeIncidents,
        resolvedIncidents: health.resolvedIncidents,
        systemUptime: health.readinessScore,
        dataCollectionRate: health.cctvCollectionRate,
        readinessStatus: health.readinessStatus,
        readinessPass: health.readinessPass,
        readinessTotal: health.readinessTotal,
        healthCheckSummary,
        centralAiJobCount: health.centralAiJobCount,
        centralAiEnabled: health.centralAiEnabled,
      });
    } catch (error) {
      if (requestId !== statsRequestIdRef.current) return;
      console.error('시스템 통계 가져오기 실패:', error);
    } finally {
      if (requestId === statsRequestIdRef.current) {
        setLoading(false);
      }
    }
  };

  const fetchPools = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('[CentralControl] Fetching pools...');
      const response: any = await poolApi.getPools();
      console.log('[CentralControl] Pools API Response:', response);

      if (response && response.success === false) {
        throw new Error(response.message || '시설 목록을 가져오기 실패');
      }

      let poolData: Pool[] = [];
      const raw = response?.data ?? response;
      if (Array.isArray(raw)) {
        poolData = raw;
      } else if (raw?.pools && Array.isArray(raw.pools)) {
        poolData = raw.pools;
      } else if (raw?.data && Array.isArray(raw.data)) {
        poolData = raw.data;
      } else if (response?.pools && Array.isArray(response.pools)) {
        poolData = response.pools;
      } else {
        console.warn('Invalid pool data received:', response);
        throw new Error(
          response?.message || '시설 목록 형식을 해석 불가'
        );
      }

      let primarySlots: { code: string; order: number }[] = [];
      try {
        const { fetchPrimaryPoolSlots } = await import('../services/dashboardSettingsService');
        primarySlots = await fetchPrimaryPoolSlots();
      } catch {
        primarySlots = [];
      }

      const poolsWithStatus = poolData.map((pool) => ({
        ...pool,
        status: normalizePoolDashboardStatus(pool),
      }));
      const { sortPoolsByPrimaryOrder } = await import('../utils/sortPoolsByPrimaryOrder');
      const ordered = sortPoolsByPrimaryOrder(poolsWithStatus, primarySlots);
      const filteredByStatus = ordered.filter(
        (pool) => filter === 'all' || pool.status === filter
      );
      setPools(ordered);
      setFilteredPools(filteredByStatus);
      console.log('[CentralControl] Updated pools state and filtered pools');
    } catch (error: any) {
      console.error('Failed to load pools:', error);
      setError(`시설 목록 로딩 실패: ${error.message || '알 수 없는 오류'}. 새 시설을 추가 가능`);
      setPools([]);
      setFilteredPools([]);
    } finally {
      setPoolsLoaded(true);
      setLoading(false);
      setLastRefreshed(new Date());
    }
  };

  const {
    isModalOpen,
    editingPool: currentEditingPool,
    openModal,
    closeModal,
    handleSubmit: handleFormSubmit,
  } = usePoolFormModal({ onSuccess: fetchPools });

  // 초기 데이터 로딩
  useEffect(() => {
    void fetchPools();
  }, [filter]);

  // 시설 목록 로드 완료 후에만 통계 갱신 (초기 [] 요청이 늦게 끝나 0으로 덮어쓰는 것 방지)
  useEffect(() => {
    if (!poolsLoaded) return;
    void fetchSystemStats(pools);
  }, [pools, poolsLoaded]);

  // 대시보드 탭으로 돌아올 때 통계 새로고침
  useEffect(() => {
    if (activeTab === 0 && poolsLoaded) {
      void fetchSystemStats(pools);
    }
  }, [activeTab]);

  const handleOpenManualAlert = (pool: Pool) => {
    const poolId = Number(pool.id);
    if (!Number.isFinite(poolId)) return;
    setManualAlertTarget({
      poolId,
      poolName: pool.name || pool.code || `시설 #${poolId}`,
      safetyPhoneRaw: String(pool.safety_phone || '').trim(),
    });
  };

  const handleManagePool = (pool: Pool) => {
    const identifier = pool.code || `id_${pool.id}`;
    console.log(`Managing pool with identifier: ${identifier}`);
    navigate(`/pool-management/${identifier}`, { 
      state: { poolId: pool.id, poolCode: pool.code }
    });
  };

  const handleMonitorPool = (pool: Pool) => {
    const identifier = pool.code || `id_${pool.id}`;
    console.log(`Monitoring pool with identifier: ${identifier}`);
    navigate(`/pool-monitoring/${identifier}`, {
      state: { poolId: pool.id, poolCode: pool.code }
    });
  };

  const handleSearchByCode = () => {
    if (!searchCode.trim()) {
      alert('코드를 입력');
      return;
    }
    const foundPool = pools.find(pool => pool.code === searchCode.trim());
    if (foundPool) {
      console.log(`Found pool with code ${searchCode}: navigating to /pool/${searchCode}`);
      navigate(`/pool/${searchCode.trim()}`);
    } else {
      console.log(`No pool found with code: ${searchCode}`);
      alert('해당 코드의 시설을 찾기 불가');
    }
  };

  const handlePoolDelete = async (pool: Pool) => {
    if (!window.confirm(`정말로 ${pool.name} 시설을 삭제 확인`)) {
      return;
    }
    setError(null);
    try {
      // standardPoolApi를 사용하여 삭제
      if (pool.id) {
        const numericId = typeof pool.id === 'string' ? parseInt(pool.id, 10) : pool.id;
        if (isNaN(numericId)) {
          throw new Error('유효하지 않은 시설 ID');
        }
        
        const response = await standardPoolApi.deletePool(numericId);
        if (response.success) {
          console.log('시설이 성공적으로 삭제되었습니다:', pool.id);
          // 삭제 성공 시 목록 새로고침
          await fetchPools();
        } else {
          throw new Error(response.message || '시설 삭제 실패');
        }
      } else if (pool.code) {
        // 코드로 삭제 시도
        const response = await standardPoolApi.deletePool(pool.code);
        if (response.success) {
          console.log('시설이 성공적으로 삭제되었습니다:', pool.code);
          // 삭제 성공 시 목록 새로고침
          await fetchPools();
        } else {
          throw new Error(response.message || '시설 삭제 실패');
        }
      } else {
        throw new Error('시설 ID 또는 코드를 찾기 불가');
      }
    } catch (error: any) {
      console.error('시설 삭제 실패:', error);
      setError(`시설 삭제 실패: ${error.message || '알 수 없는 오류'}`);
    }
  };

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = event.target.value;
    console.log('Filter changed:', value);
    setFilter(value);
  };

  const handleOpenPasswordManager = () => {
    setIsPasswordManagerOpen(true);
  };

  const handleClosePasswordManager = () => {
    setIsPasswordManagerOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleSettingsSave = (updatedSettings: SystemSettings) => {
    console.log("Saving settings:", updatedSettings);
    // settings 업데이트 로직 (이제 sendMessage 대신 API 직접 호출 필요)
    // sendMessage('update_system_settings', updatedSettings); // 주석 처리 또는 삭제
    // 예시: axios.post('/api/settings', updatedSettings).then(...);
  };

  const handleGoToSettings = () => {
    setActiveTab(2);
  };

  const handleGoToPhysiologicalMonitoring = () => {
    navigate('/physiological-monitoring');
  };

  const handlePoolUpdate = async (poolCode: string, data: any) => {
    try {
      const response = await poolApi.updatePool(Number(poolCode), data);
      if (response.success) {
        console.log('Pool updated successfully:', poolCode);
        fetchPools();
      } else {
        throw new Error(response.message || '시설 업데이트 실패');
      }
    } catch (error: any) {
      console.error('Failed to update pool:', error);
      setError(`시설 업데이트 실패: ${error.message || '알 수 없는 오류'}`);
    }
  };

  // 탭 변경 처리
  // 0 대시보드 · 1 시설 관리 · 2 시스템 · 3 리포트 · 4 카메라 배치 · 5 관제 메시지
  // 6 시설 게시판 · 7 관제 자료실 · 8 카메라 시방 자동 배치
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    if (newValue === 5) {
      navigate('/central-control?tab=messages', { replace: true });
    } else if (newValue === 6) {
      navigate('/central-control?tab=facility-board', { replace: true });
    } else if (newValue === 7) {
      navigate('/central-control?tab=control-archive', { replace: true });
    } else if (newValue === 2) {
      navigate('/central-control?tab=system', { replace: true });
    } else if (newValue === 4) {
      navigate('/central-control?tab=camera-layout', { replace: true });
    } else if (newValue === 8) {
      navigate('/central-control?tab=camera-spec-wizard', { replace: true });
    } else if (newValue === 9) {
      navigate('/central-control?tab=label-recording', { replace: true });
    } else if (newValue === 1) {
      navigate(
        poolMgmtSubTab === 1
          ? '/central-control?tab=pool-status'
          : '/central-control?tab=pools',
        { replace: true }
      );
    } else if (newValue === 3) {
      navigate('/central-control?tab=reports', { replace: true });
    } else {
      navigate('/central-control', { replace: true });
    }
    if (isMobileNav) {
      setMobileNavOpen(false);
    }
  };

  const handlePoolMgmtSubTabChange = (_: React.SyntheticEvent, value: number) => {
    setPoolMgmtSubTab(value);
    navigate(
      value === 1
        ? '/central-control?tab=pool-status'
        : '/central-control?tab=pools',
      { replace: true }
    );
  };

  const navList = (
    <List>
      {isSuperuser && (
      <>
      {authState.user?.is_brand_manager && (
      <ListItem button selected={location.pathname === '/brand-menu'} onClick={() => { navigate('/brand-menu'); if (isMobileNav) setMobileNavOpen(false); }}>
        <ListItemIcon>
          <SettingsIcon />
        </ListItemIcon>
        <ListItemText primary="메뉴 허용" />
      </ListItem>
      )}
      {allowNav('nav.dashboard') && (
      <ListItem button selected={activeTab === 0} onClick={() => handleTabChange({} as React.SyntheticEvent, 0)}>
        <ListItemIcon>
          <DashboardIcon />
        </ListItemIcon>
        <ListItemText primary="대시보드" />
      </ListItem>
      )}
      {allowNav('nav.central-monitoring') && (
      <ListItem
        button
        onClick={() => {
          openCentralMonitoringPopupWindow();
          if (isMobileNav) setMobileNavOpen(false);
        }}
      >
        <ListItemIcon>
          <VideocamIcon />
        </ListItemIcon>
        <ListItemText primary="통합 관제 모니터링" />
      </ListItem>
      )}
      {allowNav('nav.individual') && (
      <ListItem
        button
        onClick={() => {
          navigate('/individual-monitoring');
          if (isMobileNav) setMobileNavOpen(false);
        }}
      >
        <ListItemIcon>
          <VideoCameraFrontIcon />
        </ListItemIcon>
        <ListItemText primary="개별 관제 모니터링" />
      </ListItem>
      )}
      {allowNav('nav.messages') && (
      <ListItem
        button
        selected={activeTab === 5}
        onClick={() => handleTabChange({} as React.SyntheticEvent, 5)}
      >
        <ListItemIcon>
          <Badge badgeContent={unreadCount} color="error" max={99}>
            <MailOutlineIcon />
          </Badge>
        </ListItemIcon>
        <ListItemText primary="관제 메시지" />
      </ListItem>
      )}
      {allowNav('nav.facility-board') && (
      <ListItem
        button
        selected={activeTab === 6}
        onClick={() => handleTabChange({} as React.SyntheticEvent, 6)}
      >
        <ListItemIcon>
          <Badge badgeContent={boardUnreadCount} color="error" max={99}>
            <ForumIcon />
          </Badge>
        </ListItemIcon>
        <ListItemText primary="시설 게시판" />
      </ListItem>
      )}
      {allowNav('nav.archive') && (
      <ListItem
        button
        selected={activeTab === 7}
        onClick={() => handleTabChange({} as React.SyntheticEvent, 7)}
      >
        <ListItemIcon>
          <FolderSharedIcon />
        </ListItemIcon>
        <ListItemText primary="관제 자료실" />
      </ListItem>
      )}
      {allowNav('nav.pools') && (
      <ListItem button selected={activeTab === 1} onClick={() => handleTabChange({} as React.SyntheticEvent, 1)}>
        <ListItemIcon>
          <PoolIcon />
        </ListItemIcon>
        <ListItemText primary="시설 관리" />
      </ListItem>
      )}
      {allowNav('nav.system') && (
      <ListItem button selected={activeTab === 2} onClick={() => handleTabChange({} as React.SyntheticEvent, 2)}>
        <ListItemIcon>
          <SettingsIcon />
        </ListItemIcon>
        <ListItemText primary="시스템 관리" />
      </ListItem>
      )}
      {allowNav('nav.reports') && (
      <ListItem button selected={activeTab === 3} onClick={() => handleTabChange({} as React.SyntheticEvent, 3)}>
        <ListItemIcon>
          <AssessmentIcon />
        </ListItemIcon>
        <ListItemText primary="리포트" />
      </ListItem>
      )}
      {SHOW_CAMERA_LAYOUT_NAV && allowNav('nav.camera-layout') && (
      <ListItem button selected={activeTab === 4} onClick={() => handleTabChange({} as React.SyntheticEvent, 4)}>
        <ListItemIcon>
          <ViewInArIcon />
        </ListItemIcon>
        <ListItemText primary="카메라 배치·설치" />
      </ListItem>
      )}
      {allowNav('nav.camera-spec') && (
      <ListItem button selected={activeTab === 8} onClick={() => handleTabChange({} as React.SyntheticEvent, 8)}>
        <ListItemIcon>
          <AutoFixHighIcon />
        </ListItemIcon>
        <ListItemText primary="카메라 시방 자동 배치" />
      </ListItem>
      )}
      {allowNav('nav.label-recording') && (
      <ListItem button selected={activeTab === 9} onClick={() => handleTabChange({} as React.SyntheticEvent, 9)}>
        <ListItemIcon>
          <VideoLibraryIcon />
        </ListItemIcon>
        <ListItemText primary="영상 녹화" />
      </ListItem>
      )}
      </>
      )}
      {!isSuperuser && (
        <>
          {allowNav('nav.individual') && (
          <ListItem
            button
            selected={location.pathname === '/individual-monitoring'}
            onClick={() => {
              navigate('/individual-monitoring');
              if (isMobileNav) setMobileNavOpen(false);
            }}
          >
            <ListItemIcon>
              <VideoCameraFrontIcon />
            </ListItemIcon>
            <ListItemText primary="개별 관제 모니터링" />
          </ListItem>
          )}
          {allowNav('nav.messages') && (
          <ListItem
            button
            selected={location.pathname === '/operator-messages'}
            onClick={() => {
              navigate('/operator-messages');
              if (isMobileNav) setMobileNavOpen(false);
            }}
          >
            <ListItemIcon>
              <Badge badgeContent={unreadCount} color="error" max={99}>
                <MailOutlineIcon />
              </Badge>
            </ListItemIcon>
            <ListItemText primary="관제 메시지" />
          </ListItem>
          )}
        </>
      )}
    </List>
  );

  // 시간 포맷 함수
  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('ko-KR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', minHeight: '100dvh' }}>
      <AppBar position="static">
        <Toolbar sx={{ flexWrap: 'wrap', gap: 1, py: { xs: 0.5, sm: 1 } }}>
          {isMobileNav && (
            <IconButton
              color="inherit"
              edge="start"
              aria-label="메뉴"
              onClick={() => setMobileNavOpen(true)}
              sx={{ mr: 0.5 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              justifyContent: { xs: 'flex-start', sm: 'center' },
              alignItems: 'center',
              minWidth: 0,
            }}
          >
            <KymaBrandTitle variant="central" tone="onPrimary" size="large" />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: { xs: 0.5, sm: 2 },
              width: { xs: '100%', sm: 'auto' },
              justifyContent: { xs: 'flex-end', sm: 'flex-start' },
            }}
          >
            <Typography variant="caption" display="block" sx={{ whiteSpace: 'nowrap' }}>
              마지막 업데이트: {formatTime(lastRefreshed)}
            </Typography>
            <Button
              startIcon={<RefreshIcon />}
              onClick={fetchPools}
              disabled={loading}
              variant="outlined"
              size="small"
              color="primary"
            >
              새로고침
            </Button>
            <Button color="primary" onClick={handleLogout} size="small">
              로그아웃
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      <Box sx={{ display: 'flex', flexGrow: 1, minHeight: 0, minWidth: 0 }}>
        <Drawer
          variant={isMobileNav ? 'temporary' : 'permanent'}
          open={isMobileNav ? mobileNavOpen : true}
          onClose={() => setMobileNavOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            width: isMobileNav ? 0 : DRAWER_WIDTH,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
            },
          }}
        >
          <Toolbar />
          {navList}
        </Drawer>

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            minWidth: 0,
            p: { xs: 1, sm: 2 },
            overflow: 'auto',
          }}
        >
          <Toolbar sx={{ minHeight: { xs: 8, sm: '48px !important' } }} />
          {activeTab === 0 && (
            <Box>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <StatsSummaryWidget 
                    stats={systemStats} 
                    isLoading={loading} 
                    title="시스템 통계"
                    subtitle="전체 시설 및 장치 현황"
                  />
                </Grid>
                <Grid item xs={12}>
                  <EventLogWidget 
                    title="최근 이벤트"
                    subtitle="시스템에서 감지된 알림 및 이벤트"
                    maxItems={200}
                    pageSize={10}
                    showViewAll={false}
                    showBulkDelete
                  />
                </Grid>
                <Grid item xs={12}>
                  <SafetyAlertDispatchHistoryCard
                    showPoolColumn
                    pageSize={5}
                    title="현장 안전 알림 발송 이력"
                    subheader="SMS · 카카오 알림톡 · 웨어러블 — 전체 시설 발송 결과 (최근순)"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <PoolStatusWidget 
                    title="시설 상태"
                    subtitle="주요 시설 현황"
                    maxItems={4}
                    onRefresh={fetchPools}
                    isLoading={loading}
                    showEmptySettingsHint
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <SystemReadinessWidget />
                </Grid>
              </Grid>
            </Box>
          )}

          {activeTab === 1 && (
            <Box>
              <Tabs
                value={poolMgmtSubTab}
                onChange={handlePoolMgmtSubTabChange}
                sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
              >
                <Tab label="시설 목록" {...hiddenTabProps(poolSubOn(0))} />
                <Tab label="시설 상태" {...hiddenTabProps(poolSubOn(1))} />
              </Tabs>

              {poolMgmtSubTab === 0 && (
                <Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  justifyContent: 'space-between',
                  alignItems: { xs: 'stretch', md: 'center' },
                  mb: 3,
                  gap: 2,
                }}
              >
                <Box>
                  <Typography variant="body1" color="text.secondary">
                    등록된 시설: {pools.length}개
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                  <TextField
                    select
                    label="상태 필터"
                    value={filter}
                    onChange={handleFilterChange}
                    variant="outlined"
                    size="small"
                    sx={{ width: { xs: '100%', sm: 150 } }}
                  >
                    <MenuItem value="all">전체 보기</MenuItem>
                    <MenuItem value="active">활성</MenuItem>
                    <MenuItem value="inactive">비활성</MenuItem>
                    <MenuItem value="maintenance">유지보수</MenuItem>
                  </TextField>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<VideocamIcon />}
                    onClick={() => openCentralMonitoringPopupWindow()}
                  >
                    통합 관제 모니터링
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => openModal()}
                  >
                    시설 추가
                  </Button>
                </Box>
              </Box>

              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <Typography>시설 데이터를 로딩 중...</Typography>
                </Box>
              ) : filteredPools.length === 0 ? (
                <Box sx={{ textAlign: 'center', my: 4 }}>
                  <Typography variant="h6" color="text.secondary">
                    {filter === 'all'
                      ? '등록된 시설 없음'
                      : '해당 상태의 시설 없음'}
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                      <TableRow>
                        <TableCell>코드</TableCell>
                        <TableCell>이름</TableCell>
                        <TableCell>주소</TableCell>
                        <TableCell>상태</TableCell>
                        <TableCell>수용인원</TableCell>
                        <TableCell>현재인원</TableCell>
                        <TableCell>관리</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredPools.map((pool) => (
                        <TableRow key={pool.code || pool.id}>
                          <TableCell>{pool.code}</TableCell>
                          <TableCell>{pool.name}</TableCell>
                          <TableCell>{pool.address}</TableCell>
                          <TableCell>
                            <Chip 
                              label={pool.status === 'active' ? '활성' : 
                                    pool.status === 'inactive' ? '비활성' : '유지보수'} 
                              color={pool.status === 'active' ? 'success' : 
                                    pool.status === 'inactive' ? 'error' : 'warning'} 
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{pool.capacity}</TableCell>
                          <TableCell>{pool.current_occupancy}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Tooltip title="알림 메시지 전송">
                                <IconButton
                                  onClick={() => handleOpenManualAlert(pool)}
                                  size="small"
                                  color="primary"
                                >
                                  <NotificationsActiveIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="모니터링">
                                <IconButton onClick={() => handleMonitorPool(pool)} size="small">
                                  <VisibilityIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="시설 관리">
                                <IconButton onClick={() => handleManagePool(pool)} size="small">
                                  <SettingsIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="시설 삭제">
                                <IconButton onClick={() => handlePoolDelete(pool)} size="small">
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
                </Box>
              )}

              {poolMgmtSubTab === 1 && (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    등록된 모든 시설의 실시간 요약
                  </Typography>
                  <PoolStatusWidget
                    title="전체 시설"
                    subtitle="등록된 시설 현황"
                    showAllPools
                    maxItems={999}
                    onRefresh={fetchPools}
                    isLoading={loading}
                  />
                </Box>
              )}
            </Box>
          )}

          {activeTab === 2 && (
            <Box>
              <SystemManagement />
            </Box>
          )}

          {activeTab === 3 && (
            <Box>
              <CentralReportsPage />
            </Box>
          )}

          {activeTab === 4 && (
            <Box>
              <CameraLayoutPlannerPage pools={pools} loading={loading} />
            </Box>
          )}

          {activeTab === 8 && (
            <Box>
              <CameraSpecWizardPage pools={pools} loading={loading} />
            </Box>
          )}

          {activeTab === 9 && (
            <Box>
              <LabelRecordingPanel pools={pools} />
            </Box>
          )}

          {activeTab === 5 && (
            <Box>
              <OperatorMessagesPanel embedded />
            </Box>
          )}
          {activeTab === 6 && (
            <Box>
              <FacilityBoardPanel embedded pools={pools} />
            </Box>
          )}
          {activeTab === 7 && (
            <Box>
              <ControlArchivePanel embedded />
            </Box>
          )}
        </Box>
      </Box>

      <PoolForm
        open={isModalOpen}
        onClose={closeModal}
        onSubmit={async (data) => {
          console.log('[CentralControl] PoolForm onSubmit 호출됨. 제출 데이터:', data);
          try {
            await handleFormSubmit(data);
            console.log('[CentralControl] handleFormSubmit 완료 후 fetchPools 호출');
            // 300ms 지연 후 데이터 다시 가져오기
            setTimeout(() => {
              fetchPools();
            }, 300);
          } catch (err) {
            console.error('[CentralControl] 폼 제출 중 오류:', err);
          }
        }}
        pool={currentEditingPool}
      />

      <Dialog 
        open={isPasswordManagerOpen} 
        onClose={handleClosePasswordManager} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: { minHeight: '80vh' }
        }}
      >
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', fontSize: '1.5rem' }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              <VpnKeyIcon sx={{ mr: 1 }} />
              비밀번호 관리 시스템
            </Box>
            <IconButton 
              edge="end" 
              color="inherit" 
              onClick={handleClosePasswordManager} 
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {isPasswordManagerOpen ? <PasswordManager /> : null}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={handleClosePasswordManager} 
            variant="contained" 
            color="primary" 
            size="large"
            startIcon={<CloseIcon />}
          >
            닫기
          </Button>
        </DialogActions>
      </Dialog>

      <PoolManualAlertDialog
        open={manualAlertTarget != null}
        target={manualAlertTarget}
        onClose={() => setManualAlertTarget(null)}
        onSent={() => setManualAlertSnack('알림 메시지를 전송함')}
      />

      <Snackbar
        open={Boolean(manualAlertSnack)}
        autoHideDuration={4000}
        onClose={() => setManualAlertSnack(null)}
        message={manualAlertSnack ?? ''}
      />
    </Box>
  );
};

export default CentralControl;